import type { UseMutationResult } from '@tanstack/react-query'
import type { CountryCode, DebtorBase, Identifier } from './debtorModels'
import type { Currency } from './moneyModel'
import { toCurrency } from './moneyModel'
import type { Root } from './rootModel'
import type { Address } from './addressModels'
import type { AttachmentWithIdsGateway } from './uploadModels'
import type { Guarantor } from './companyModel'

export const creditStatuses = ['PROCESSING', 'ONGOING_REVISION', 'REVISED', 'ACCEPTED', 'REFUSED'] as const
export type CreditStatus = (typeof creditStatuses)[number]

export const supportingDocuments = ['FINANCIAL_STATEMENTS', 'FUNDRAISING_PROOF', 'OTHER_DOCUMENT'] as const
export type SupportingDocument = (typeof supportingDocuments)[number]

// The credit worker `CreditLimitGuarantorSchema` keeps the currency as a string.
export type CreditLimitAllocationGateway = {
    guarantor: Guarantor
    claimedAmount: number | null
    amount: number | null
    currency: string
    outstandingAmount: number | null
    reservedAmount: number | null
}

// The service narrows the currency. An unknown code renders with no currency, the same as a missing one.
export type CreditLimitAllocation = Omit<CreditLimitAllocationGateway, 'currency'> & { currency: Currency | undefined }

export const freezeReasonCodes = ['AWAITING_REPAYMENTS', 'FRAUD_SCORE_INCREASED'] as const
export type FreezeReasonCode = (typeof freezeReasonCodes)[number]
export type FreezeReason = { code: FreezeReasonCode }

// makalu-api models/CreditLimit.ts `CreditLimitSchema`, as makalu maps it.
export type Credit = Root & {
    amount: number | null
    claimedAmount?: number
    outstandingAmount?: number
    reservedAmount?: number | null
    currency: Currency | null
    claimedCurrency: Currency
    status: CreditStatus
    // creditLimits/registerCreditLimit.api.ts creates the limit with a `RegistrationNumberSchema`.
    debtorIdentifier: Identifier
    debtor?: DebtorBase
    debtorId: string | null
    refusalReasons?: { code: CreditLimitRefusalReasons }[] | null
    // Makalu computes it from the amount and the outstanding amount.
    remainingAmount?: number
    allocations: CreditLimitAllocation[]
    frozenAt: string | null // ISO 8601 date
    freezeReasons: FreezeReason[]
    possibleRevision: boolean | null
    missingSupportingDocuments: SupportingDocument[]
}

export type CreditGateway = Omit<Credit, 'allocations' | 'remainingAmount'> & {
    allocations: CreditLimitAllocationGateway[]
}

// Credits list build before to send batch of credits mutations
export type PendingCredit = Pick<Credit, 'claimedAmount' | 'claimedCurrency' | 'debtorId'> & {
    country: CountryCode
    name: string | null
    debtorIdentifier: Identifier
    requestStatus?: UseMutationResult['status']
}

export type CreatePendingCredit = Pick<
    PendingCredit,
    'name' | 'claimedAmount' | 'claimedCurrency' | 'debtorIdentifier'
> & {
    country: CountryCode
    address: Omit<Address, 'country'>
}

/*
 * creditLimits/registerCreditLimit.api.ts `BodySchema`. packages/json-schema `validateOrFail` coerces a null
 * amount to 0 and a null comment to an empty string, which is what makalu relies on.
 */
export type RegisterCreditLimitBody = {
    claimedAmount?: number | null
    claimedCurrency: Currency
    debtorIdentifier: Identifier
    debtorId?: string
    comment?: string | null
    attachments?: AttachmentWithIdsGateway[]
    guarantor?: Guarantor
}

export type CreateCreditFields = Pick<
    RegisterCreditLimitBody,
    'claimedAmount' | 'claimedCurrency' | 'debtorIdentifier'
> & {
    debtorId: string | null | undefined
}

export type CreateCreditGateway = RegisterCreditLimitBody & { guarantor: Guarantor }

export type UpdateCreditGateway = RegisterCreditLimitBody

export type UpdateCredit = Pick<
    RegisterCreditLimitBody,
    'claimedAmount' | 'claimedCurrency' | 'debtorIdentifier' | 'guarantor'
> & {
    debtorId: string | null | undefined
    comment?: string
    attachments?: File[]
}

export const creditLimitRefusalReasons = [
    'DEBTOR_NOT_ELIGIBLE',
    /** @deprecated To be removed in favor of more specific reasons below. */
    'DEBTOR_NOT_FOUND_OR_CLOSED',
    'DEBTOR_NOT_FOUND',
    'DEBTOR_CLOSED',
    'OTHER_REASON',
    'NEWLY_ESTABLISHED_DEBTOR',
    'COLLECTIVE_PROCEDURE',
    'WRONG_OR_MISSING_DEBTOR_NAME',
    'WRONG_OR_MISSING_DEBTOR_ADDRESS',
    'WRONG_DEBTOR_IDENTIFIER',
    'MISSING_SUPPORTING_DOCUMENTS',
    'PAYMENT_BEHAVIOR_ISSUE'
] as const
export type CreditLimitRefusalReasons = (typeof creditLimitRefusalReasons)[number]

export const selectableCreditLimitRefusalReasons = creditLimitRefusalReasons.filter(
    (reason) => reason !== 'DEBTOR_NOT_FOUND_OR_CLOSED'
)

export const toCredit = (credit: CreditGateway): Credit => ({
    ...credit,
    remainingAmount:
        credit.amount && typeof credit.outstandingAmount === 'number'
            ? credit.amount - credit.outstandingAmount
            : undefined,
    allocations: credit.allocations.map((allocation) => ({ ...allocation, currency: toCurrency(allocation.currency) }))
})
